/**
 * useRefData — on-demand loader for ref-master category options.
 *
 * Resolution order (cheapest first):
 *   1. In-memory Zustand store (refStore)  — no I/O
 *   2. IndexedDB ref-cache store           — fast local read
 *   3. Remote API (single or batch)        — network call
 *
 * Results are written to both IDB and refStore so subsequent calls
 * (even after component unmount/remount) are served from cache.
 */

import { useCallback, useState } from 'react'
import { useRefStore } from '@/store/refStore'
import { fetchRefSingle, fetchRefBatch, fetchUsageSubtypes, type RefOption } from '@/services/refService'
import { getRefCacheRecord, setRefCacheRecord } from '@/services/idbService'
import { log } from '@/utils/logger'

export function useRefData() {
  const { setCache } = useRefStore()
  const [isLoading, setIsLoading] = useState(false)

  const loadCategory = useCallback(
    async (category: string): Promise<void> => {
      if (useRefStore.getState().cache[category]) return

      setIsLoading(true)
      try {
        // Check IndexedDB
        try {
          const record = await getRefCacheRecord(category)
          if (record?.options?.length) {
            log.info('refData IDB hit', { category, count: String(record.options.length) })
            setCache(category, record.options)
            return
          }
        } catch {
          // IDB read failure is non-fatal — fall through to API
        }

        // Fetch from API
        try {
          const options = await fetchRefSingle(category)
          if (options.length) {
            setCache(category, options)
            setRefCacheRecord({ category, options, cachedAt: Date.now() }).catch((err) =>
              log.warn('refData IDB write failed', { category, error: String(err) })
            )
          } else {
            log.warn('refData fetchRefSingle returned empty options', { category })
          }
        } catch (err) {
          log.error(`refData failed to load category "${category}"`, err instanceof Error ? err : new Error(String(err)), { category })
        }
      } finally {
        setIsLoading(false)
      }
    },
    [setCache]
  )

  /**
   * Live lookup of Subtype values for a Use Type/Type label. Unlike loadCategory,
   * this always hits the API (never IDB/memory-only) because the category name is
   * derived from the label and can't be known in advance — the backend itself is
   * cache-first (ref_master, including a "no subtypes" sentinel), so a repeat call
   * for a value already checked is cheap and just returns the cached options.
   * Returns the resolved category + options so callers can decide whether the
   * Subtype dropdown should be shown.
   */
  const loadUsageSubtypes = useCallback(
    async (vehicleType: 'TT' | 'TR', label: string): Promise<{ category: string; options: RefOption[] }> => {
      setIsLoading(true)
      try {
        const { category, options } = await fetchUsageSubtypes(vehicleType, label)
        if (category) {
          setCache(category, options)
          setRefCacheRecord({ category, options, cachedAt: Date.now() }).catch((err) =>
            log.warn('refData IDB write failed (usage subtypes)', { category, error: String(err) })
          )
        }
        return { category, options }
      } catch (err) {
        log.error('refData failed to load usage subtypes', err instanceof Error ? err : new Error(String(err)), { vehicleType, label })
        return { category: '', options: [] }
      } finally {
        setIsLoading(false)
      }
    },
    [setCache]
  )

  const loadBatch = useCallback(
    async (categories: string[]): Promise<void> => {
      if (categories.length === 0) return

      const currentCache = useRefStore.getState().cache
      const notInMemory = categories.filter((c) => !currentCache[c])
      if (notInMemory.length === 0) return

      setIsLoading(true)
      try {
        // Check IDB for each missing category
        const stillMissing: string[] = []
        await Promise.all(
          notInMemory.map(async (category) => {
            try {
              const record = await getRefCacheRecord(category)
              if (record?.options?.length) {
                log.info('refData IDB hit (batch)', { category, count: String(record.options.length) })
                setCache(category, record.options)
              } else {
                stillMissing.push(category)
              }
            } catch {
              stillMissing.push(category)
            }
          })
        )

        if (stillMissing.length === 0) return

        log.info('refData batch API fetch', { categoryCount: String(stillMissing.length) })

        // Batch fetch remaining from API
        try {
          const results = await fetchRefBatch(stillMissing)
          for (const [category, options] of Object.entries(results)) {
            if (options?.length) {
              setCache(category, options)
              setRefCacheRecord({ category, options, cachedAt: Date.now() }).catch((err) =>
                log.warn('refData IDB write failed (batch)', { category, error: String(err) })
              )
            }
          }
        } catch (err) {
          log.error('refData batch fetch failed', err instanceof Error ? err : new Error(String(err)), { categoryCount: String(stillMissing.length) })
        }
      } finally {
        setIsLoading(false)
      }
    },
    [setCache]
  )

  return { loadCategory, loadBatch, loadUsageSubtypes, isLoading }
}
